import aiLogger from './ai-logger';
import { Component } from "./component";
import { ChatThemeModal } from "./chat-theme-modal";
import { escapeHtml } from '../services/ai-editor-utils';

interface Conversation {
    id: number;
    title: string;
    last_prompt_id: string;
    last_activity_at: string;
    messages_count: number;
    created_at: string;
}

export class ChatSidebar extends Component {
    protected conversations: Conversation[] = [];
    protected activeConversationId: number | null = null;
    protected isOpen: boolean = false;
    protected isGuest: boolean = false;
    
    protected listContainer!: HTMLDivElement;
    protected toggleButton!: HTMLButtonElement;
    protected newConversationButton!: HTMLButtonElement;
    protected searchInput!: HTMLInputElement;
    protected sidebarOverlay!: HTMLDivElement;
    protected themeModal!: ChatThemeModal;
    
    // Lazy loading state
    protected isLoading: boolean = false;
    protected hasMore: boolean = true;
    protected offset: number = 0;
    protected limit: number = 11; // Quantidade que cabe na tela
    protected loadingTimeout: any = null;
    protected scrollListener: any = null;

    constructor() {
        super();
        // Garantir inicialização do array
        this.conversations = [];
    }

    setup() {
        // Inicializar array explicitamente
        this.conversations = [];
        this.activeConversationId = null;
        this.isGuest = this.$opts.userLoggedIn !== 'true';
        this.isOpen = false;
        
        this.listContainer = this.$refs.list as HTMLDivElement;
        this.newConversationButton = this.$refs.newConversation as HTMLButtonElement;
        this.searchInput = this.$refs.search as HTMLInputElement;
        
        // Toggle fica dentro do aside (para acompanhar o translateX) mas posicionado fora visualmente
        this.toggleButton = this.$el.querySelector('.sidebar-toggle-mobile') as HTMLButtonElement;
        // Overlay fica fora do aside
        this.sidebarOverlay = document.querySelector('.sidebar-overlay') as HTMLDivElement;
        
        // Verificar se elementos existem
        if (!this.listContainer || !this.toggleButton || !this.newConversationButton || !this.searchInput || !this.sidebarOverlay) {
            aiLogger.error('ChatSidebar: Elementos de referência não encontrados');
            return;
        }

        // Inicializar modal de tema com as preferências salvas vindas via options
        this.themeModal = new ChatThemeModal({
            savedBgColor:     this.$opts.savedBgColor     || '',
            savedTextColor:   this.$opts.savedTextColor   || '',
            savedCustomBg:    this.$opts.savedCustomBg    || '',
            savedCustomText:  this.$opts.savedCustomText  || '',
            savedContainerBg: this.$opts.savedContainerBg || '',
            savedCardBg:      this.$opts.savedCardBg      || '',
            savedBtnColor:    this.$opts.savedBtnColor    || '',
            savedBubbleUser:     this.$opts.savedBubbleUser     || '',
            savedBubbleAi:       this.$opts.savedBubbleAi       || '',
            savedInputBg:        this.$opts.savedInputBg        || '',
            savedConvCardBg:     this.$opts.savedConvCardBg     || '',
            savedConvCardBorder: this.$opts.savedConvCardBorder || '',
            savedConvCardText:   this.$opts.savedConvCardText   || '',
            savedConvCardBtn:    this.$opts.savedConvCardBtn    || '',
            savedFileCardBg:     this.$opts.savedFileCardBg     || '',
            savedFileCardBorder: this.$opts.savedFileCardBorder || '',
            savedFileCardText:   this.$opts.savedFileCardText   || '',
            savedFileCardBtn:    this.$opts.savedFileCardBtn    || '',
            savedBubbleUserText: this.$opts.savedBubbleUserText || '',
            savedBubbleAiText:   this.$opts.savedBubbleAiText   || '',
            savedLogoMode:       this.$opts.savedLogoMode       || '',
            savedConvCardActiveBg:     this.$opts.savedConvCardActiveBg     || '',
            savedConvCardActiveBorder: this.$opts.savedConvCardActiveBorder || '',
            savedConvCardActiveText:   this.$opts.savedConvCardActiveText   || '',
            savedFileCardActiveBg:     this.$opts.savedFileCardActiveBg     || '',
            savedFileCardActiveBorder: this.$opts.savedFileCardActiveBorder || '',
            savedFileCardActiveText:   this.$opts.savedFileCardActiveText   || '',
            savedModalBg:        this.$opts.savedModalBg        || '',
            savedModalBackdrop:  this.$opts.savedModalBackdrop  || '',
            savedModalBorder:    this.$opts.savedModalBorder    || '',
            savedModalText:      this.$opts.savedModalText      || '',
            savedModalHeaderBg:  this.$opts.savedModalHeaderBg  || '',
        });

        // Botão tema
        const themeBtn = this.$refs.themeBtn as HTMLButtonElement | null;
        if (themeBtn) {
            themeBtn.addEventListener('click', () => this.themeModal.open());
        }
        
        // Event listeners
        this.toggleButton.addEventListener('click', () => this.toggleSidebar());
        this.newConversationButton.addEventListener('click', () => this.createConversation());
        this.searchInput.addEventListener('input', (e) => this.handleSearch((e.target as HTMLInputElement).value));
        this.sidebarOverlay.addEventListener('click', () => this.closeSidebar());
        
        // Scroll listener para lazy loading
        this.scrollListener = () => this.handleScroll();
        this.listContainer.addEventListener('scroll', this.scrollListener);
        
        // Load initial data
        this.loadConversations(true);

        // Escutar evento de nova conversa criada pelo chat.
        // Para usuários autenticados: recarrega a lista da API.
        // Para guests (API retorna vazio): adiciona o card diretamente com os dados do evento,
        // evitando que a conversa desapareça da sidebar por falta de persistência server-side.
        document.addEventListener('chat-conversation-created', ((e: CustomEvent) => {
            const convId: number = e.detail?.conversationId;
            const convTitle: string = e.detail?.title || 'Nova Conversa';
            if (!convId) return;

            this.loadConversations(true).then(() => {
                const existsInDom = !!this.listContainer.querySelector(`[data-conversation-id="${convId}"]`);
                if (!existsInDom) {
                    // Fallback para guests: API retornou vazio, cria o card localmente.
                    const guestConv: Conversation = {
                        id: convId,
                        title: convTitle,
                        last_prompt_id: '',
                        last_activity_at: new Date().toISOString(),
                        messages_count: 1,
                        created_at: new Date().toISOString(),
                    };
                    const item = this.createConversationItem(guestConv, true);
                    if (this.listContainer.firstChild) {
                        this.listContainer.insertBefore(item, this.listContainer.firstChild);
                    } else {
                        this.listContainer.appendChild(item);
                    }
                    this.conversations.unshift(guestConv);
                }
                this.markConversationActive(convId);
            });
        }) as EventListener);

        // Escutar evento de título atualizado
        document.addEventListener('chat-conversation-title-updated', ((e: CustomEvent) => {
            if (e.detail?.conversationId && e.detail?.title) {
                this.updateConversationTitleInList(e.detail.conversationId, e.detail.title);
            }
        }) as EventListener);
    }

    async loadConversations(reset: boolean = false) {
        if (this.isLoading) return;
        if (!this.hasMore && !reset) return;
        
        if (reset) {
            this.offset = 0;
            this.conversations = [];
            this.hasMore = true;
        }
        
        this.isLoading = true;
        
        // Delay de 300ms antes de mostrar loading (só se não for primeira carga)
        if (!reset && this.offset > 0) {
            this.loadingTimeout = setTimeout(() => {
                this.showLoadingIndicator();
            }, 300);
        }
        
        try {
            const params = new URLSearchParams({
                limit: this.limit.toString(),
                offset: this.offset.toString(),
            });

            // Fallback para guests: envia o session_id do localStorage/sessionStorage
            // para que o servidor identifique o guest mesmo sem cookie.
            if (this.isGuest) {
                const localSessionId = this.getGuestSessionId();
                if (localSessionId) params.set('guest_session_id', localSessionId);
            }

            const response = await window.$http.get(`/chat/conversations?${params}`);
            const data = (response.data || response) as any;
            const newConversations = Array.isArray(data.conversations) ? data.conversations : [];
            
            // Adicionar novas conversas com animação
            if (reset) {
                this.conversations = newConversations;
            } else {
                this.conversations.push(...newConversations);
            }
            
            this.hasMore = data.has_more ?? false;
            this.offset += newConversations.length;
            
            this.renderList(reset);
        } catch (error) {
            window.$events.error('Erro ao carregar conversas.');
            aiLogger.error(error);
            if (!Array.isArray(this.conversations)) {
                this.conversations = [];
            }
        } finally {
            if (this.loadingTimeout) {
                clearTimeout(this.loadingTimeout);
                this.loadingTimeout = null;
            }
            this.hideLoadingIndicator();
            this.isLoading = false;
        }
    }

    renderList(animate: boolean = false) {
        // Não limpar o container em append mode, apenas adicionar novos
        const searchValue = this.searchInput.value.toLowerCase();
        const isSearching = searchValue.length > 0;
        
        // Se está buscando, limpar e renderizar tudo
        if (isSearching) {
            this.listContainer.innerHTML = '';
            const filtered = this.conversations.filter(c => 
                c.title.toLowerCase().includes(searchValue)
            );
            
            if (filtered.length === 0) {
                this.listContainer.innerHTML = '<div class="sidebar-empty">Nenhuma conversa encontrada</div>';
                return;
            }
            
            filtered.forEach(conversation => {
                const item = this.createConversationItem(conversation, false);
                this.listContainer.appendChild(item);
            });
            return;
        }
        
        // Garantir que conversations é um array
        if (!Array.isArray(this.conversations)) {
            this.conversations = [];
        }
        
        if (this.conversations.length === 0) {
            this.listContainer.innerHTML = '<div class="sidebar-empty">Nenhuma conversa encontrada</div>';
            return;
        }
        
        // Limpar apenas se for reset
        if (animate) {
            this.listContainer.innerHTML = '';
        }
        
        // Renderizar todas as conversas (lazy loading já limitou no backend)
        this.conversations.forEach((conversation, index) => {
            // Verificar se já existe no DOM
            const existingItem = this.listContainer.querySelector(`[data-conversation-id="${conversation.id}"]`);
            if (existingItem) return; // Já renderizado
            
            const item = this.createConversationItem(conversation, animate);
            this.listContainer.appendChild(item);
        });
    }

    createConversationItem(conversation: Conversation, animate: boolean = false): HTMLElement {
        const item = document.createElement('div');
        item.className = 'sidebar-conversation-item';
        item.setAttribute('data-conversation-id', conversation.id.toString());
        
        if (conversation.id === this.activeConversationId) {
            item.classList.add('active');
        }
        
        if (animate) {
            item.classList.add('fade-in');
        }
        
        const msgCount = conversation.messages_count ?? 0;
        const dateStr = this.formatDate(conversation.last_activity_at || conversation.created_at);
        
        item.innerHTML = `
            <div class="conversation-content">
                <div class="conversation-title">${escapeHtml(conversation.title || 'Nova Conversa')}</div>
                <div class="conversation-meta">
                    <span class="conversation-count">${msgCount} msgs</span>
                    <span class="conversation-date">${dateStr}</span>
                </div>
            </div>
            <div class="conversation-actions">
                <button class="btn-icon btn-edit" title="Renomear" data-id="${conversation.id}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                </button>
                <button class="btn-icon btn-delete" title="Deletar" data-id="${conversation.id}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                </button>
            </div>
        `;
        
        // Event listeners
        item.querySelector('.conversation-content')?.addEventListener('click', () => {
            this.selectConversation(conversation.id);
        });
        
        item.querySelector('.btn-edit')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showRenameModal(conversation);
        });
        
        item.querySelector('.btn-delete')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.deleteConversation(conversation.id);
        });
        
        return item;
    }

    createSkeletonItem(): HTMLElement {
        const item = document.createElement('div');
        item.className = 'sidebar-conversation-item skeleton-loading';
        item.setAttribute('data-temp-id', 'creating');
        
        item.innerHTML = `
            <div class="conversation-content">
                <div class="conversation-title skeleton-line"></div>
                <div class="conversation-meta">
                    <span class="conversation-count skeleton-line-sm"></span>
                    <span class="conversation-date skeleton-line-sm"></span>
                </div>
            </div>
            <div class="conversation-actions">
                <button class="btn-icon btn-edit" disabled>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                </button>
                <button class="btn-icon btn-delete" disabled>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                </button>
            </div>
        `;
        
        return item;
    }

    async createConversation() {
        // Criar item skeleton no topo
        const skeletonItem = this.createSkeletonItem();
        
        // Adicionar no início da lista
        if (this.listContainer.firstChild) {
            this.listContainer.insertBefore(skeletonItem, this.listContainer.firstChild);
        } else {
            this.listContainer.appendChild(skeletonItem);
        }
        
        try {
            const response = await window.$http.post('/chat/conversations', {
                title: 'Nova Conversa',
            });
            
            const data = response.data || response;
            const conversation = data.conversation;
            if (!conversation) {
                aiLogger.error('ChatSidebar: Resposta inválida ao criar conversa', data);
                skeletonItem.remove();
                return;
            }
            
            this.conversations.unshift(conversation);
            
            const realItem = this.createConversationItem(conversation, true);
            
            this.listContainer.replaceChild(realItem, skeletonItem);
            
            this.selectConversation(conversation.id);
            window.$events.success('Nova conversa criada!');
        } catch (error) {
            skeletonItem.remove();
            window.$events.error('Erro ao criar conversa.');
            aiLogger.error(error);
        }
    }

    deleteConversation(id: number) {
        this.showConfirmModal(
            'Excluir Conversa',
            'Deseja realmente excluir esta conversa? Esta ação não pode ser desfeita.',
            async () => {
                const itemToRemove = this.listContainer.querySelector(`[data-conversation-id="${id}"]`) as HTMLElement;
                if (!itemToRemove) return;
                
                // Adicionar estado de "deletando"
                itemToRemove.classList.add('deleting');
                
                // Desabilitar todos os botões e cliques
                const buttons = itemToRemove.querySelectorAll('button');
                buttons.forEach(btn => (btn as HTMLButtonElement).disabled = true);
                
                const content = itemToRemove.querySelector('.conversation-content') as HTMLElement;
                if (content) {
                    content.style.pointerEvents = 'none';
                }
                
                try {
                    if (!this.isGuest) {
                        await (window.$http as any).delete(`/chat/conversations/${id}`);
                    }

                    this.conversations = this.conversations.filter(c => c.id !== id);

                    // Animação de remoção
                    itemToRemove.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
                    itemToRemove.style.opacity = '0';
                    itemToRemove.style.transform = 'translateX(-20px)';

                    setTimeout(() => {
                        itemToRemove.remove();

                        if (this.conversations.length === 0) {
                            this.listContainer.innerHTML = '<div class="sidebar-empty">Nenhuma conversa encontrada</div>';
                        }
                    }, 200);

                    if (this.activeConversationId === id) {
                        this.activeConversationId = null;
                        this.$emit('conversation-deleted', { id });
                    }

                    window.$events.success('Conversa excluída.');
                } catch (error) {
                    // Remover estado de "deletando" em caso de erro
                    itemToRemove.classList.remove('deleting');
                    buttons.forEach(btn => (btn as HTMLButtonElement).disabled = false);
                    if (content) {
                        content.style.pointerEvents = '';
                    }

                    window.$events.error('Erro ao excluir conversa.');
                    aiLogger.error(error);
                }
            }
        );
    }

    showRenameModal(conversation: Conversation) {
        this.showInputModal(
            'Renomear Conversa',
            'Novo título:',
            conversation.title,
            async (newTitle: string) => {
                if (!newTitle || newTitle === conversation.title) return;
                try {
                    if (!this.isGuest) {
                        await (window.$http as any).patch(`/chat/conversations/${conversation.id}`, {
                            title: newTitle,
                        });
                    }

                    // Atualiza o array interno e o DOM diretamente — sem re-render completo.
                    // renderList(false) skip itens já existentes no DOM, então o título nunca atualizava.
                    this.updateConversationTitleInList(conversation.id, newTitle);

                    window.$events.success('Conversa renomeada!');
                } catch (error) {
                    window.$events.error('Erro ao renomear conversa.');
                    aiLogger.error(error);
                }
            }
        );
    }

    /**
     * Atualiza o título de uma conversa específica na lista (sem recarregar tudo)
     */
    updateConversationTitleInList(conversationId: number, newTitle: string): void {
        // Atualizar no array de conversas
        const index = this.conversations.findIndex(c => c.id === conversationId);
        if (index !== -1) {
            this.conversations[index].title = newTitle;
        }

        // Atualizar diretamente no DOM (mais eficiente que renderList completo)
        const conversationItem = this.listContainer.querySelector(`[data-conversation-id="${conversationId}"]`);
        if (conversationItem) {
            const titleElement = conversationItem.querySelector('.conversation-title');
            if (titleElement) {
                titleElement.textContent = newTitle;
            }
        }
    }

    /**
     * Exibe um modal de confirmação customizado (substitui confirm() nativo)
     */
    private showConfirmModal(title: string, message: string, onConfirm: () => void): void {
        this.removeModal();

        const overlay = document.createElement('div');
        overlay.className = 'sidebar-modal-overlay';
        overlay.innerHTML = `
            <div class="sidebar-modal">
                <div class="sidebar-modal-header">
                    <h4>${escapeHtml(title)}</h4>
                </div>
                <div class="sidebar-modal-body">
                    <p>${escapeHtml(message)}</p>
                </div>
                <div class="sidebar-modal-footer">
                    <button class="sidebar-modal-btn sidebar-modal-btn-cancel">Cancelar</button>
                    <button class="sidebar-modal-btn sidebar-modal-btn-danger">Excluir</button>
                </div>
            </div>
        `;

        overlay.querySelector('.sidebar-modal-btn-cancel')?.addEventListener('click', () => this.removeModal());
        overlay.querySelector('.sidebar-modal-btn-danger')?.addEventListener('click', () => {
            this.removeModal();
            onConfirm();
        });
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) this.removeModal();
        });

        document.body.appendChild(overlay);
        requestAnimationFrame(() => overlay.classList.add('visible'));
    }

    /**
     * Exibe um modal de input customizado (substitui prompt() nativo)
     */
    private showInputModal(title: string, label: string, currentValue: string, onConfirm: (value: string) => void): void {
        this.removeModal();

        const overlay = document.createElement('div');
        overlay.className = 'sidebar-modal-overlay';
        overlay.innerHTML = `
            <div class="sidebar-modal">
                <div class="sidebar-modal-header">
                    <h4>${escapeHtml(title)}</h4>
                </div>
                <div class="sidebar-modal-body">
                    <label class="sidebar-modal-label">${escapeHtml(label)}</label>
                    <input type="text" class="sidebar-modal-input" value="${escapeHtml(currentValue)}" maxlength="255" />
                </div>
                <div class="sidebar-modal-footer">
                    <button class="sidebar-modal-btn sidebar-modal-btn-cancel">Cancelar</button>
                    <button class="sidebar-modal-btn sidebar-modal-btn-primary">Salvar</button>
                </div>
            </div>
        `;

        const input = overlay.querySelector('.sidebar-modal-input') as HTMLInputElement;
        overlay.querySelector('.sidebar-modal-btn-cancel')?.addEventListener('click', () => this.removeModal());
        overlay.querySelector('.sidebar-modal-btn-primary')?.addEventListener('click', () => {
            const val = input.value.trim();
            this.removeModal();
            if (val) onConfirm(val);
        });
        input.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                const val = input.value.trim();
                this.removeModal();
                if (val) onConfirm(val);
            } else if (e.key === 'Escape') {
                this.removeModal();
            }
        });
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) this.removeModal();
        });

        document.body.appendChild(overlay);
        requestAnimationFrame(() => {
            overlay.classList.add('visible');
            input.focus();
            input.select();
        });
    }

    /**
     * Remove qualquer modal ativo
     */
    private removeModal(): void {
        document.querySelector('.sidebar-modal-overlay')?.remove();
    }

    markConversationActive(id: number) {
        this.listContainer.querySelectorAll('.sidebar-conversation-item').forEach(item => {
            item.classList.remove('active');
        });
        const selectedItem = this.listContainer.querySelector(`[data-conversation-id="${id}"]`);
        if (selectedItem) {
            selectedItem.classList.add('active');
        }
        this.activeConversationId = id;
    }

    /**
     * Exibe um badge "Interrompida" no card da conversa que teve o stream abortado
     * por troca de conversa. O badge é removido quando o usuário selecionar o card.
     */
    private getGuestSessionId(): string | null {
        for (const storage of [localStorage, sessionStorage]) {
            try {
                const id = storage.getItem('athena_guest_session_id');
                if (id) return id;
            } catch { /* blocked */ }
        }
        return null;
    }

    markConversationInterrupted(conversationId: number): void {
        const item = this.listContainer.querySelector(`[data-conversation-id="${conversationId}"]`);
        if (!item) return;

        // Evita duplicar o badge
        if (item.querySelector('.conversation-interrupted-badge')) return;

        const meta = item.querySelector('.conversation-meta');
        if (meta) {
            const badge = document.createElement('span');
            badge.className = 'conversation-interrupted-badge';
            badge.textContent = '⚠ Interrompida';
            meta.appendChild(badge);
        }
    }

    selectConversation(id: number) {
        // Remover classe active de todos os itens
        this.listContainer.querySelectorAll('.sidebar-conversation-item').forEach(item => {
            item.classList.remove('active');
        });

        // Adicionar classe active ao item selecionado
        const selectedItem = this.listContainer.querySelector(`[data-conversation-id="${id}"]`);
        if (selectedItem) {
            selectedItem.classList.add('active');
            // Remove o badge de interrupção ao selecionar a conversa
            selectedItem.querySelector('.conversation-interrupted-badge')?.remove();
        }

        this.activeConversationId = id;
        this.$emit('conversation-selected', { id });
        this.closeSidebar(); // Fechar sidebar em mobile
    }

    toggleSidebar() {
        this.isOpen = !this.isOpen;
        this.$el.classList.toggle('open', this.isOpen);
        this.sidebarOverlay.classList.toggle('visible', this.isOpen);
    }

    closeSidebar() {
        this.isOpen = false;
        this.$el.classList.remove('open');
        this.sidebarOverlay.classList.remove('visible');
    }

    async handleSearch(query: string) {
        const trimmed = query.trim();
        
        // Se está buscando, fazer uma nova requisição SEM paginação
        if (trimmed.length > 0) {
            this.isLoading = true;
            try {
                const params = new URLSearchParams({ search: trimmed });
                if (this.isGuest) {
                    const localSessionId = this.getGuestSessionId();
                    if (localSessionId) params.set('guest_session_id', localSessionId);
                }
                const response = await window.$http.get(`/chat/conversations?${params}`);
                const data = (response.data || response) as any;
                this.conversations = Array.isArray(data.conversations) ? data.conversations : [];
                this.hasMore = false; // Desabilitar lazy load durante busca
                this.renderList(false);
            } catch (error) {
                aiLogger.error('Erro na busca:', error);
            } finally {
                this.isLoading = false;
            }
        } else {
            // Voltar para modo normal (recarregar com paginação)
            this.loadConversations(true);
        }
    }
    
    handleScroll() {
        // Verificar se chegou perto do fim (100px antes do final)
        const container = this.listContainer;
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        
        if (distanceFromBottom < 100 && this.hasMore && !this.isLoading) {
            this.loadConversations(false);
        }
    }
    
    showLoadingIndicator() {
        // Verificar se já existe
        if (this.listContainer.querySelector('.sidebar-loading')) return;
        
        const loading = document.createElement('div');
        loading.className = 'sidebar-loading';
        loading.innerHTML = `
            <div class="sidebar-loading-spinner"></div>
            <span>Carregando outras conversas...</span>
        `;
        this.listContainer.appendChild(loading);
    }
    
    hideLoadingIndicator() {
        this.listContainer.querySelector('.sidebar-loading')?.remove();
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return 'agora';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return 'agora';
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 60) return `${diffMins}m atrás`;
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h atrás`;
        return date.toLocaleDateString('pt-BR');
    }
}
